import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import useAuthStore from '../stores/authStore';
import useAdminAuthStore, { isAdminUser } from '../stores/adminAuthStore';
import './VerifyEmail.css';

export default function EmailVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [status, setStatus] = useState(token ? 'verifying' : 'idle');
  const [message, setMessage] = useState(token ? 'Verifying your email...' : '');
  const { setAuth } = useAuthStore();
  const setAdminAuth = useAdminAuthStore((state) => state.setAuth);
  const verificationAttempted = useRef(false);

  // Auto-verify if token is present
  useEffect(() => {
    if (!token || verificationAttempted.current) return;

    verificationAttempted.current = true;

    const verifyEmail = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });

        const data = await response.json();

        if (!response.ok) {
          setStatus('error');
          setMessage(data.error || 'Email verification failed');
          return;
        }

        const admin = isAdminUser(data.user);
        const isEmployer = data.user?.accountType === 'employer';
        const destination = admin
          ? '/admin-cofounder-hubs'
          : isEmployer
            ? '/post-job'
            : '/onboarding';

        setStatus('success');
        setMessage(
          admin
            ? 'Email verified successfully! Redirecting to admin...'
            : `Email verified successfully! Redirecting to ${isEmployer ? 'job posting' : 'onboarding'}...`
        );

        if (admin) {
          setAdminAuth(data.user, data.accessToken);
        } else {
          setAuth(data.user, data.accessToken);
        }

        setTimeout(() => {
          navigate(destination);
        }, 2000);
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage('An error occurred during verification');
      }
    };

    verifyEmail();
  }, [token, navigate, setAuth, setAdminAuth]);

  const handleResendSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage('Please enter your email address');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        setMessage(data.error || 'Failed to resend verification email');
        setStatus('error');
        return;
      }

      setMessage('Verification email sent! Please check your inbox.');
      setStatus('success');
      setEmail('');
    } catch (error) {
      console.error('Resend verification error:', error);
      setMessage('Network error. Please try again.');
      setStatus('error');
    }
  };

  // Auto-verify view (when token is in URL)
  if (token) {
    return (
      <div className="verify-email-container">
        <div className="verify-email-card">
          {status === 'verifying' && (
            <>
              <div className="spinner"></div>
              <h2>Verifying Your Email</h2>
              <p>{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="success-icon">✓</div>
              <h2>Email Verified!</h2>
              <p>{message}</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="error-icon">✕</div>
              <h2>Verification Failed</h2>
              <p>{message}</p>
              <button onClick={() => navigate('/signup')} className="btn-primary">
                Back to Sign Up
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Resend verification view (manual form)
  return (
    <div className="auth-container">
      <Link to="/" className="auth-back-link">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Back to Home
      </Link>

      <div className="auth-box">
        <div className="auth-header">
          <Link to="/" className="auth-logo-link">
            <h1 className="auth-logo">CofounderHubs</h1>
          </Link>
          <h2 className="auth-title">Verify Your Email</h2>
        </div>

        <div className="resend-verification-content">
          <p className="resend-subtitle">
            Your email hasn't been verified yet. We'll send you a new verification link.
          </p>

          <form onSubmit={handleResendSubmit} className="auth-form">
            {message && (
              <div className={`auth-message ${status === 'success' ? 'success' : 'error'}`}>
                {message}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                disabled={status === 'loading'}
              />
            </div>

            <button 
              type="submit" 
              className="auth-button" 
              disabled={status === 'loading'}
            >
              {status === 'loading' ? 'Sending...' : 'Resend Verification Email'}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              Already verified?{' '}
              <Link to="/signin" className="auth-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
